import { useEffect, useState } from "react";
import {
  DRIVE_SCOPE,
  buildBundle,
  ensureBackupFolder,
  uploadBackup,
  listBackups,
  downloadBackup,
  deleteBackup,
  type BackupFile,
} from "@agencylens/drive-backup";
import { Button } from "@/components/ui/Button.js";
import {
  loadConnectionRaw,
  readAccessToken,
  readRefreshToken,
  saveGoogleConnection,
  deleteConnection,
  isExpired,
} from "@/lib/connections.js";
import { connectGoogle, refreshGoogleToken } from "@/lib/oauth.js";
import { idbGet, idbList, idbPut } from "@/lib/idb.js";
import { useApp } from "@/store/app.js";
import { getSession } from "@/lib/session.js";

export function DrivePanel({
  onError,
  configReady,
}: {
  onError: (msg: string | undefined) => void;
  configReady: boolean;
}) {
  const session = getSession();
  const { driveConnection, driveBackups, setDriveConnection, setDriveBackups } = useApp();
  const [busy, setBusy] = useState<string | undefined>();
  const [folderId, setFolderId] = useState<string | undefined>();

  useEffect(() => {
    (async () => {
      const rec = await loadConnectionRaw("google-drive", "default");
      if (rec) setDriveConnection(rec);
    })().catch((e) => onError(String(e)));
  }, [setDriveConnection, onError]);

  useEffect(() => {
    if (!driveConnection) return;
    (async () => {
      try {
        setBusy("Loading Drive backups…");
        const token = await ensureFreshToken();
        const fid = await ensureBackupFolder(token);
        setFolderId(fid);
        const backups = await listBackups(token, fid);
        setDriveBackups(backups);
      } catch (e) {
        onError(`Could not list Drive backups: ${String(e)}`);
      } finally {
        setBusy(undefined);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driveConnection?.updatedAt]);

  async function ensureFreshToken(): Promise<string> {
    const rec = await loadConnectionRaw("google-drive", "default");
    if (!rec) throw new Error("No Drive connection");
    if (!isExpired(rec)) return readAccessToken(rec);
    const refresh = await readRefreshToken(rec);
    if (!refresh) throw new Error("Token expired and no refresh token. Reconnect Drive.");
    const refreshed = await refreshGoogleToken(refresh);
    const newRec = await saveGoogleConnection("google-drive", "default", refreshed);
    setDriveConnection(newRec);
    return readAccessToken(newRec);
  }

  async function onConnect() {
    onError(undefined);
    setBusy("Opening Google sign-in…");
    try {
      const tokens = await connectGoogle({ scope: DRIVE_SCOPE });
      const rec = await saveGoogleConnection("google-drive", "default", tokens);
      setDriveConnection(rec);
    } catch (e) {
      onError(`Drive OAuth failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onDisconnect() {
    await deleteConnection("google-drive", "default");
    setDriveConnection(undefined);
    setDriveBackups([]);
    setFolderId(undefined);
  }

  async function onBackupNow() {
    if (!folderId) return;
    onError(undefined);
    setBusy("Bundling and uploading…");
    try {
      const token = await ensureFreshToken();
      // Gather all IndexedDB keys we want to back up: connections + uploads.
      const keys = await idbList();
      const backupKeys = keys.filter(
        (k) => k.startsWith("connections:") || k.startsWith("uploads:") || k === "agency-brand",
      );
      const records: Record<string, unknown> = {};
      for (const k of backupKeys) {
        const v = await idbGet(k);
        if (v !== undefined) records[k] = v;
      }
      const bundle = buildBundle({
        records,
        ownerEmail: session?.payload.email,
        source: navigator.userAgent.slice(0, 100),
      });
      const created = await uploadBackup(token, folderId, bundle);
      setDriveBackups([created, ...driveBackups]);
    } catch (e) {
      onError(`Backup failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onRestore(file: BackupFile) {
    if (
      !window.confirm(
        `Restore from ${file.name}? This overwrites the connection records and uploads in your current browser. The current data is NOT backed up first.`,
      )
    ) {
      return;
    }
    onError(undefined);
    setBusy("Restoring…");
    try {
      const token = await ensureFreshToken();
      const bundle = await downloadBackup(token, file.id);
      // Write records back to IndexedDB
      for (const [k, v] of Object.entries(bundle.records)) {
        await idbPut(k, v);
      }
      onError("Restore complete. Reloading…");
      window.setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      onError(`Restore failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onDelete(file: BackupFile) {
    if (!window.confirm(`Delete ${file.name} from Drive? This cannot be undone.`)) {
      return;
    }
    onError(undefined);
    setBusy("Deleting…");
    try {
      const token = await ensureFreshToken();
      await deleteBackup(token, file.id);
      setDriveBackups(driveBackups.filter((f: BackupFile) => f.id !== file.id));
    } catch (e) {
      onError(`Delete failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
      <h2 className="mb-3 text-lg font-medium">Google Drive backup</h2>
      <p className="mb-4 text-sm text-slate-600">
        Mirror your AgencyLens configuration (connections, uploads, settings)
        to a folder in your own Google Drive. We use the narrow{" "}
        <code>drive.file</code> scope — we can only see files we created.
      </p>

      {driveConnection ? (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Connected
            </span>
            <Button onClick={onBackupNow} disabled={busy !== undefined || !folderId}>
              Back up now
            </Button>
            <Button variant="ghost" size="sm" onClick={onDisconnect} disabled={busy !== undefined}>
              Disconnect
            </Button>
            {busy && <span className="text-sm text-slate-500">{busy}</span>}
          </div>

          {driveBackups.length === 0 ? (
            <p className="text-sm text-slate-500">No backups yet. Click "Back up now" to create one.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Backup</th>
                    <th className="py-2 pr-4 font-medium">Modified</th>
                    <th className="py-2 pr-4 font-medium">Size</th>
                    <th className="py-2 pr-4 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {driveBackups.map((f) => (
                    <tr key={f.id} className="border-b border-slate-100">
                      <td className="py-2 pr-4 font-mono text-xs">{f.name}</td>
                      <td className="py-2 pr-4 text-xs text-slate-500">
                        {f.modifiedAt
                          ? new Date(f.modifiedAt).toLocaleString()
                          : "—"}
                      </td>
                      <td className="py-2 pr-4 text-xs text-slate-500">
                        {f.sizeBytes ? `${(f.sizeBytes / 1024).toFixed(1)} KB` : "—"}
                      </td>
                      <td className="py-2 pr-4 text-right">
                        <button
                          onClick={() => onRestore(f)}
                          className="mr-3 text-xs text-brand-700 underline"
                          disabled={busy !== undefined}
                        >
                          Restore
                        </button>
                        <button
                          onClick={() => onDelete(f)}
                          className="text-xs text-red-700 underline"
                          disabled={busy !== undefined}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <Button onClick={onConnect} disabled={busy !== undefined || !configReady}>
          Connect Google Drive
        </Button>
      )}
    </section>
  );
}
